import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import './adminDashBoard.css';
import DashboardSummary from './dashboardSummary';
import './dashboardSummary.css';
import AllUsers from './allUsers';
import AllAdmins from './allAdmins';
import AllOrders from './allOrders';
import OrderAnalytics from './orderAnalytics';
import AllInquiries from './allInquiries';
import InquiryDetails from './inquiryDetails';
import ProcessOrder from './processOrder';
import AddNewCar from './addNewCar';
import GeographicalSetup from './geographicalSetup';
import AddShipmentFee from './addShipmentFee';
import FeesConfigurationList from './feesConfigurationList';
import AllCarListed from './allCarListed';
import AddNewCategory from './addNewCategory';
import AddNewBrand from './addNewBrand';
import AddNewAccessories from './addnewAccessories';
import AllTickets from './allTickets';
import AllReviews from './allReviews';
import DataManagementUI from './dataManagementUI';
import AddContainerFee from './addContainerFee';
import AddIndividualFee from './addIndividualFee';
import SendFinalPerformaInvoice from './sendFinalPerformaInvoice';
import AllFinalPerformaInvoice from './allFinalPerformaInvoice';
import ManagePopularCars from './managePopularCars';
import ManageAffordableCars from './manageAffordableCars';
import SetPriceMargin from './setPriceMargin';
import AdminProfile from './adminProfile';
import NotificationsPage from './notificationsPage';
import AdminChat from './adminChat';

// ── Module-level cache for notification counts ────────────────────────────
// Same idea as dashboardSummary.js: survives unmount/remount so switching
// menus and coming back doesn't reset the bell/sidebar badges to 0 briefly.
let notifCountsCache = null;
const NOTIF_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const getCachedNotif = (fallback) =>
  (notifCountsCache && Date.now() - notifCountsCache.timestamp < NOTIF_CACHE_TTL)
    ? notifCountsCache.data
    : fallback;

// Same WebSocket endpoint used by adminChat.js — used here to keep the
// message-icon badge's unread-message count live instead of relying on a
// 30s poll (which was both slow to reflect new messages AND could show a
// stale "unread" badge for a ticket the admin had already read).
const WS_URL = 'https://car.amar.co.jp/api/dashboard/ws-chat';

// ── Role-based access control (roleId based, matches /roles API) ──────────
// roleId 1 = SUPER_ADMIN, roleId 2 = ADMIN → unrestricted, unchanged behavior.
// Every other roleId only sees the sidebar menu keys listed here. Roles not
// yet listed default to an empty array (no section assigned yet) — add them
// one at a time as each admin type is rolled out (Inquiry Admin first).
const FULL_ACCESS_ROLE_IDS = [1, 2];
const ROLE_ALLOWED_MENUS = {
  4: ['inquiries'], // INQUIRY_ADMIN — All Inquiries, Send Invoice
  5: ['orders'],    // ORDER_ADMIN — All Orders, All Final Invoices, Order Analytics, Process Order
  6: ['tickets'],   // TICKET_ADMIN — All Tickets
  7: ['reviews'],   // REVIEW_ADMIN — All Reviews
};
// These aren't sidebar sections — they're the chat icon, bell icon, and the
// My Profile / Change Password dropdown. Every admin needs these regardless
// of role, so they're exempt from the ROLE_ALLOWED_MENUS restriction.
const ALWAYS_ACCESSIBLE_MENUS = ['adminChat', 'notifications', 'adminProfile'];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [activeSubMenu, setActiveSubMenu] = useState(null);
  const [adminName, setAdminName] = useState('Admin User');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminRole, setAdminRole] = useState('');
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [profileTab, setProfileTab] = useState('profile');
  const [selectedInquiry, setSelectedInquiry] = useState(() => {
    try {
      const saved = sessionStorage.getItem('po_selectedInquiry');
      return saved ? JSON.parse(saved) : null;
    } catch (e) { return null; }
  });
  const [selectedInquiryForInvoice, setSelectedInquiryForInvoice] = useState(() => {
    try {
      const saved = sessionStorage.getItem('sfpi_inquiryForInvoice');
      return saved ? JSON.parse(saved) : null;
    } catch (e) { return null; }
  });
  const [selectedInvoiceForOrder, setSelectedInvoiceForOrder] = useState(() => {
    try {
      const saved = sessionStorage.getItem('po_invoiceForOrder');
      return saved ? JSON.parse(saved) : null;
    } catch (e) { return null; }
  });
  const [profileImage, setProfileImage] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [autoOpenId, setAutoOpenId] = useState(null);
  const [selectedInvoiceForEdit, setSelectedInvoiceForEdit] = useState(() => {
    try {
      const saved = sessionStorage.getItem('sfpi_invoiceForEdit');
      return saved ? JSON.parse(saved) : null;
    } catch (e) { return null; }
  });

  // ── Notification counts (bell icon + sidebar menu badges) ─────────────────
  const [notifCounts, setNotifCounts] = useState(() => getCachedNotif({
    orders: 0,         // New Order count — real count from isOrderRead field
    inquiries: 0,      // Shipping Inquiries "New" count — real count from isInquiryRead field
    users: 0,          // Total Users -> New Users
    inquiryTickets: 0, // Inquiry Tickets -> New
    orderTickets: 0,   // Order Tickets -> New
    reviews: 0,        // Total Reviews -> Pending
    ticketMessages: 0, // Unread chat messages across inquiry + order tickets
  }));
  // Bell badge shows the unread count from the Notifications page's own API
  // (global-notification/get-all-list) — separate from the per-card counts above.
  const [bellUnreadCount, setBellUnreadCount] = useState(() => getCachedNotif({ bellUnreadCount: 0 }).bellUnreadCount || 0);
  // Latest push from /topic/admin-notifications — forwarded as a prop to
  // NotificationsPage so its list stays live too (bell was already live via
  // this same event; the list itself only fetched once on mount before).
  const [liveGlobalNotif, setLiveGlobalNotif] = useState(null);
  // ticketNumber → unread message count, kept live via /topic/recent-chat.
  const perTicketUnreadRef = useRef({});
  const ticketMessagesSeededRef = useRef(false);
  // globalNotificationId → isRead, kept live via /topic/admin-notifications.
  // Needed so a folded chat-notification push (same id, backend just updated
  // the row) doesn't get counted as a brand-new unread — only a genuinely
  // new id should bump bellUnreadCount.
  const globalNotifMapRef = useRef({});

  // AdminChat calls this immediately after a successful mark-read call —
  // backend doesn't re-broadcast /topic/recent-chat for mark-read actions
  // (only for new messages), so without this the bell would stay stuck
  // showing the old unread count even after the admin has actually seen it.
  const handleTicketMarkedRead = (ticketNumber) => {
    if (!ticketNumber) return;
    perTicketUnreadRef.current[ticketNumber] = 0;
    const total = Object.values(perTicketUnreadRef.current).reduce((s, n) => s + n, 0);
    setNotifCounts(prev => ({ ...prev, ticketMessages: total }));
  };
  const ticketsNotifCount = notifCounts.inquiryTickets + notifCounts.orderTickets;

  const API_BASE_URL = 'https://car.amar.co.jp/api/authorization/user';

  const getAuthToken = () => localStorage.getItem('adminAuthToken');
  // Read directly from localStorage (not the `adminRole` state) — the WS
  // effect below fires on mount, same commit as the effect that loads
  // `adminRole` into state, so the state value would still be stale here.
  const getAdminRoleName = () => localStorage.getItem('adminRole');

  // ── Role-based access helpers ──────────────────────────────────────────
  const getAdminRoleId = () => {
    const raw = localStorage.getItem('adminRoleId');
    const parsed = raw ? parseInt(raw, 10) : null;
    return Number.isNaN(parsed) ? null : parsed;
  };
  const hasFullAccess = (roleId) => FULL_ACCESS_ROLE_IDS.includes(roleId);
  // null return = unrestricted (full access role). Array = exact allow-list.
  const getAllowedMenus = (roleId) => {
    if (hasFullAccess(roleId)) return null;
    return ROLE_ALLOWED_MENUS[roleId] || [];
  };
  const canAccessMenu = (menuKey) => {
    if (ALWAYS_ACCESSIBLE_MENUS.includes(menuKey)) return true;
    const allowed = getAllowedMenus(getAdminRoleId());
    if (allowed === null) return true;
    return allowed.includes(menuKey);
  };

  const fetchProfileImage = async () => {
    try {
      const token = getAuthToken();
      if (!token) return;
      const response = await fetch(`${API_BASE_URL}/get-image/`, {
        method: 'GET',
        headers: { 'accept': 'application/json', 'Accept-Language': 'en', 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        setProfileImage(URL.createObjectURL(blob));
      }
    } catch (error) {
      console.error('Error fetching profile image:', error);
    }
  };

  const uploadProfileImage = async (file) => {
    try {
      setIsUploadingImage(true);
      const token = getAuthToken();
      if (!token) return;
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = async () => {
        const base64Image = reader.result.split(',')[1];
        const response = await fetch(`${API_BASE_URL}/upload/profile-photo`, {
          method: 'POST',
          headers: { 'accept': 'application/json', 'Accept-Language': 'en', 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ 'profile-image': base64Image })
        });
        if (response.ok) { await fetchProfileImage(); alert('Profile image updated successfully!'); }
        else alert('Failed to upload profile image');
      };
      reader.onerror = () => alert('Error reading file');
    } catch (error) {
      console.error('Error uploading profile image:', error);
      alert('Error uploading profile image');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) { alert('Please select an image file'); return; }
      if (file.size > 5 * 1024 * 1024) { alert('Image size should be less than 5MB'); return; }
      uploadProfileImage(file);
    }
  };

  // Sync state with URL
  useEffect(() => {
    const path = location.pathname.replace('/adminDashBoard/', '').replace('/adminDashBoard', '');
    if (!path || path === '' || path === 'dashboard') {
      setActiveMenu('dashboard'); setActiveSubMenu(null);
    } else if (path.startsWith('users/')) {
      setActiveMenu('users'); setActiveSubMenu(path.split('/')[1]);
    } else if (path.startsWith('orders/')) {
      setActiveMenu('orders'); setActiveSubMenu(path.split('/')[1]);
    } else if (path.startsWith('inquiries/')) {
      setActiveMenu('inquiries'); setActiveSubMenu(path.split('/')[1]);
    } else if (path.startsWith('reviews/')) {
      setActiveMenu('reviews'); setActiveSubMenu(path.split('/')[1]);
    } else if (path.startsWith('cars/')) {
      setActiveMenu('cars'); setActiveSubMenu(path.split('/')[1]);
    } else if (path.startsWith('shipment/')) {
      setActiveMenu('shipment'); setActiveSubMenu(path.split('/')[1]);
    } else if (path === 'priceMargin') {
      setActiveMenu('priceMargin'); setActiveSubMenu(null);
    } else {
      setActiveMenu(path); setActiveSubMenu(null);
    }
  }, [location.pathname]);

  // Restricted roles: bounce away from any menu not in their allow-list.
  // Runs whenever activeMenu changes, so it catches sidebar clicks AND
  // someone directly typing/bookmarking a URL for a section they can't see.
  useEffect(() => {
    const roleId = getAdminRoleId();
    if (hasFullAccess(roleId)) return;
    if (!activeMenu || activeMenu === 'noAccess') return;
    if (canAccessMenu(activeMenu)) return;

    const allowed = getAllowedMenus(roleId);
    const fallbackMenu = allowed && allowed.length > 0 ? allowed[0] : null;

    if (fallbackMenu === 'inquiries') {
      setActiveMenu('inquiries'); setActiveSubMenu('allInquiries');
      navigate('/adminDashBoard/inquiries/allInquiries');
    } else if (fallbackMenu) {
      setActiveMenu(fallbackMenu); setActiveSubMenu(null);
      navigate(`/adminDashBoard/${fallbackMenu}`);
    } else {
      // No section mapped yet for this role.
      setActiveMenu('noAccess'); setActiveSubMenu(null);
    }
  }, [activeMenu]);

  useEffect(() => {
    const storedAdminName = localStorage.getItem('adminName');
    const storedAdminEmail = localStorage.getItem('adminEmail');
    const storedAdminRole = localStorage.getItem('adminRole');
    if (storedAdminName) setAdminName(storedAdminName);
    if (storedAdminEmail) setAdminEmail(storedAdminEmail);
    if (storedAdminRole) setAdminRole(storedAdminRole);
    if (!localStorage.getItem('adminAuthToken')) { navigate('/adminLogin'); }
    else { fetchProfileImage(); }
  }, [navigate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProfileDropdown && !event.target.closest('.user-profile')) setShowProfileDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfileDropdown]);

  // ── Fetch notification counts for bell icon + sidebar badges ──────────────
  useEffect(() => {
    const fetchNotificationCounts = async () => {
      const token = getAuthToken();
      try {
        const [usersRes, inquiryTicketsRes, orderTicketsRes, reviewsRes, allInquiriesRes, allOrdersRes] = await Promise.allSettled([
          fetch('https://car.amar.co.jp/api/authorization/admin-user/get-all-user-and-admins', {
            headers: { 'accept': 'application/json', 'Accept-Language': 'en', 'Authorization': `Bearer ${token}` }
          }).then(r => r.ok ? r.json() : null).catch(() => null),
          fetch('https://car.amar.co.jp/api/dashboard/raise-ticket/get-all-tickets', {
            headers: { 'accept': 'application/json', 'Authorization': `Bearer ${token}` }
          }).then(r => r.ok ? r.json() : null).catch(() => null),
          fetch('https://car.amar.co.jp/api/dashboard/order-ticket/get-all-ticket', {
            headers: { 'accept': 'application/json' }
          }).then(r => r.ok ? r.json() : null).catch(() => null),
          fetch('https://car.amar.co.jp/api/review-rating/review-rating/admin/all-reviews?page=0&size=9999', {
            headers: { 'accept': 'application/json' }
          }).then(r => r.ok ? r.json() : null).catch(() => null),
          fetch('https://car.amar.co.jp/api/authorization/admin-dashboard-panel/get-all-inquiry?page=0&size=1000', {
            headers: { 'accept': 'application/json', 'Accept-Language': 'en' }
          }).then(r => r.ok ? r.json() : null).catch(() => null),
          fetch('https://car.amar.co.jp/api/authorization/admin-all-orders/placed-orders?page=0&size=1000', {
            headers: { 'accept': 'application/json' }
          }).then(r => r.ok ? r.json() : null).catch(() => null),
        ]);

        const usersData = usersRes.status === 'fulfilled' ? usersRes.value : null;
        const inquiryTicketsData = inquiryTicketsRes.status === 'fulfilled' ? inquiryTicketsRes.value : null;
        const orderTicketsData = orderTicketsRes.status === 'fulfilled' ? orderTicketsRes.value : null;
        const reviewsData = reviewsRes.status === 'fulfilled' ? reviewsRes.value : null;
        const allInquiriesData = allInquiriesRes.status === 'fulfilled' ? allInquiriesRes.value : null;
        const allOrdersData = allOrdersRes.status === 'fulfilled' ? allOrdersRes.value : null;

        // New users
        const userList = usersData?.userList || [];
        const newUsersCount = userList.filter(u => u.isNewUser === true).length;

        // "New" ticket status = OPEN (same logic as dashboardSummary.js)
        const getTicketStatus = (t) => {
          const s = (t.ticketStatus || '').toUpperCase();
          if (s === 'REOPEN') return 're-open';
          if (s === 'CLOSED') return 'closed';
          if (s === 'IN_PROGRESS') return 'in-progress';
          if (s === 'OPEN' && (t.ticketReopenBy || t.reopenCount > 0)) return 're-open';
          if (s === 'OPEN') return 'open';
          return 'open';
        };

        const inquiryTicketList = inquiryTicketsData?.data || [];
        const newInquiryTicketsCount = inquiryTicketList.filter(t => getTicketStatus(t) === 'open').length;

        const orderTicketList = orderTicketsData?.data || [];
        const newOrderTicketsCount = orderTicketList.filter(t => getTicketStatus(t) === 'open').length;

        // Pending reviews
        const reviewList = reviewsData?.data?.reviews || [];
        const pendingReviewsCount = reviewList.filter(r => (r.reviewStatus || '').toUpperCase() === 'PENDING').length;

        // Unread inquiries (same isInquiryRead field used in allInquiries.js / dashboardSummary.js)
        const allInquiryList = allInquiriesData?.data || [];
        const unreadInquiriesCount = allInquiryList.filter(inq => inq.isInquiryRead === false).length;

        // Unread orders (same isOrderRead field used in allOrders.js / dashboardSummary.js)
        const allOrderList = allOrdersData?.data || [];
        const unreadOrdersCount = allOrderList.filter(o => o.isOrderRead === false).length;

        // Unread chat messages across inquiry + order tickets — the initial
        // number is seeded from recent-chat/get-all (same endpoint
        // adminChat.js's list uses); after that, the dedicated WebSocket
        // effect below keeps this live via /topic/recent-chat, so this poll
        // loop deliberately does NOT touch notifCounts.ticketMessages again
        // (see newNotifCounts below — it's intentionally omitted there).
        if (!ticketMessagesSeededRef.current) {
          try {
            const recentChatRes = await fetch('https://car.amar.co.jp/api/dashboard/recent-chat/get-all?ticketType=ALL', {
              headers: { 'accept': 'application/json' },
            });
            if (recentChatRes.ok) {
              const recentChatData = await recentChatRes.json();
              (recentChatData?.data || []).forEach(c => {
                const tn = c.ticketNumber ?? c.orderTicketNumber;
                if (tn) perTicketUnreadRef.current[tn] = c.unreadCount || 0;
              });
              const seededTotal = Object.values(perTicketUnreadRef.current).reduce((s, n) => s + n, 0);
              setNotifCounts(prev => ({ ...prev, ticketMessages: seededTotal }));
            }
          } catch (seedErr) {
            console.error('Error seeding ticket message counts:', seedErr);
          }
          ticketMessagesSeededRef.current = true;
        }

        const newNotifCounts = {
          users: newUsersCount,
          inquiryTickets: newInquiryTicketsCount,
          orderTickets: newOrderTicketsCount,
          reviews: pendingReviewsCount,
          inquiries: unreadInquiriesCount,
          orders: unreadOrdersCount,
          // ticketMessages intentionally omitted — WebSocket-owned, see above.
        };
        setNotifCounts(prev => ({ ...prev, ...newNotifCounts }));

        // Bell badge count comes from the same API notificationsPage.js
        // uses — now requires the Authorization header since the backend
        // filters this list by the caller's role/identity.
        let newBellUnreadCount = 0;
        try {
          const globalNotifRes = await fetch('https://car.amar.co.jp/api/dashboard/global-notification/get-all-list', {
            headers: { 'accept': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
          });
          if (globalNotifRes.ok) {
            const globalNotifData = await globalNotifRes.json();
            const list = Array.isArray(globalNotifData) ? globalNotifData : [];
            // Full resync — this is the source of truth for read/read-all,
            // since those actions aren't broadcast over WS (see WS effect below).
            const freshMap = {};
            list.forEach(n => { freshMap[n.globalNotificationId] = n.read === true; });
            globalNotifMapRef.current = freshMap;
            newBellUnreadCount = list.filter(n => n.read === false).length;
          }
        } catch (bellErr) {
          console.error('Error fetching global notifications for bell count:', bellErr);
        }
        setBellUnreadCount(newBellUnreadCount);

        const currentTicketMessages = Object.values(perTicketUnreadRef.current).reduce((s, n) => s + n, 0);
        notifCountsCache = {
          timestamp: Date.now(),
          data: { ...newNotifCounts, ticketMessages: currentTicketMessages, bellUnreadCount: newBellUnreadCount },
        };
      } catch (error) {
        console.error('Error fetching notification counts:', error);
      }
    };

    let isMounted = true;
    let notifTimeoutId = null;

    const runAndRescheduleNotif = async () => {
      await fetchNotificationCounts();
      // Only schedule the next cycle AFTER this one fully finishes — prevents
      // overlapping cycles from piling up requests if a fetch ever runs long.
      if (isMounted) {
        notifTimeoutId = setTimeout(runAndRescheduleNotif, 30000);
      }
    };

    runAndRescheduleNotif();

    return () => {
      isMounted = false;
      if (notifTimeoutId) clearTimeout(notifTimeoutId);
    };
  }, []);

  // ── WebSocket: keep the message-icon badge live, no polling ──────────────
  useEffect(() => {
    const token = getAuthToken();
    const client = new Client({
      webSocketFactory: () => {
        const url = token ? `${WS_URL}?token=${encodeURIComponent(token)}` : WS_URL;
        return new SockJS(url);
      },
      connectHeaders: token ? { Authorization: `Bearer ${token}` } : {},
      reconnectDelay: 5000,
    });

    client.onConnect = () => {
      client.subscribe('/topic/recent-chat', (frame) => {
        let payload;
        try { payload = JSON.parse(frame.body); } catch { return; }
        const ticketNumber = payload.ticketNumber ?? payload.orderTicketNumber;
        if (!ticketNumber) return;

        perTicketUnreadRef.current[ticketNumber] = payload.unreadCount ?? 0;
        const total = Object.values(perTicketUnreadRef.current).reduce((s, n) => s + n, 0);
        setNotifCounts(prev => ({ ...prev, ticketMessages: total }));
      });

      // Bell badge — live instead of waiting for the 30s poll. Backend sends
      // one push per event on this topic, INCLUDING folded chat-message
      // updates (same ticket, same globalNotificationId, just a newer
      // message text). Upsert by id — do NOT blindly push/increment, or a
      // folded update would look like a brand-new unread notification.
      // Per-role topic (was the shared '/topic/admin-notifications') — the
      // backend now fans events out per role, e.g. SUPER_ADMIN/ADMIN still
      // get everything, INQUIRY_ADMIN only gets inquiry-related pushes.
      const myRoleName = getAdminRoleName();
      if (!myRoleName) {
        // Session predates this change (logged in before roleName started
        // being stored) — nothing we can subscribe to until they log in
        // again. Bell badge just won't update live until then.
        console.warn('[Bell WS] No adminRole in localStorage — skipping notifications subscription. Re-login to fix.');
      } else {
        client.subscribe(`/topic/notifications/role/${myRoleName}`, (frame) => {
          let dto;
          try { dto = JSON.parse(frame.body); } catch { return; }
          const id = dto.globalNotificationId;
          if (id == null) return;

          globalNotifMapRef.current[id] = dto.read === true;
          const unread = Object.values(globalNotifMapRef.current).filter(isRead => !isRead).length;
          setBellUnreadCount(unread);

          // Forward the raw push so the Notifications page (if mounted, or
          // whenever it mounts next) can upsert it without a full refetch.
          setLiveGlobalNotif(dto);
        });
      }
    };

    client.onStompError = (frame) => console.error('[Bell WS] STOMP error:', frame);

    client.activate();

    return () => {
      client.deactivate();
    };
  }, []);

  const handleMenuClick = (menu, submenu = null) => {
    setActiveMenu(menu);
    setActiveSubMenu(submenu);
    if (submenu) navigate(`/adminDashBoard/${menu}/${submenu}`);
    else navigate(`/adminDashBoard/${menu}`);
  };

  const handleConvertToOrder = (inquiry) => {
    setSelectedInquiry(inquiry);
    try { sessionStorage.setItem('po_selectedInquiry', JSON.stringify(inquiry)); } catch (e) {}
    setActiveMenu('orders'); setActiveSubMenu('processOrder');
    navigate('/adminDashBoard/orders/processOrder');
  };

  const handleBackFromProcessOrder = () => {
    setSelectedInquiry(null);
    try { sessionStorage.removeItem('po_selectedInquiry'); } catch (e) {}
    setActiveMenu('inquiries'); setActiveSubMenu('allInquiries');
    navigate('/adminDashBoard/inquiries/allInquiries');
  };

  const handleSendInvoice = (inquiry) => {
    setSelectedInquiryForInvoice(inquiry);
    try { sessionStorage.setItem('sfpi_inquiryForInvoice', JSON.stringify(inquiry)); } catch (e) {}
    setActiveMenu('inquiries'); setActiveSubMenu('sendInvoice');
    navigate('/adminDashBoard/inquiries/sendInvoice');
  };

  const handleEditInvoice = (invoiceData) => {
    setSelectedInvoiceForEdit(invoiceData);
    try { sessionStorage.setItem('sfpi_invoiceForEdit', JSON.stringify(invoiceData)); } catch (e) {}
    setActiveMenu('inquiries'); setActiveSubMenu('sendInvoice');
    navigate('/adminDashBoard/inquiries/sendInvoice');
  };

  const handleBackFromEditInvoice = () => {
    setSelectedInvoiceForEdit(null);
    try { sessionStorage.removeItem('sfpi_invoiceForEdit'); } catch (e) {}
    setActiveMenu('orders'); setActiveSubMenu('allFinalInvoices');
    navigate('/adminDashBoard/orders/allFinalInvoices');
  };

  const handleBackFromInvoice = () => {
    setSelectedInquiryForInvoice(null);
    try { sessionStorage.removeItem('sfpi_inquiryForInvoice'); } catch (e) {}
    setActiveMenu('inquiries'); setActiveSubMenu('allInquiries');
    navigate('/adminDashBoard/inquiries/allInquiries');
  };

  const handleConvertInvoiceToOrder = (invoiceData) => {
    setSelectedInvoiceForOrder(invoiceData);
    try { sessionStorage.setItem('po_invoiceForOrder', JSON.stringify(invoiceData)); } catch (e) {}
    setActiveMenu('orders'); setActiveSubMenu('processOrder');
    navigate('/adminDashBoard/orders/processOrder');
  };

  const handleBackFromInvoiceOrder = () => {
    setSelectedInvoiceForOrder(null);
    try { sessionStorage.removeItem('po_invoiceForOrder'); } catch (e) {}
    setActiveMenu('orders'); setActiveSubMenu('allFinalInvoices');
    navigate('/adminDashBoard/orders/allFinalInvoices');
  };

  const handleNavigateToInquiry = (inquiryId) => {
    setAutoOpenId(inquiryId);
    setActiveMenu('inquiries'); setActiveSubMenu('allInquiries');
    navigate('/adminDashBoard/inquiries/allInquiries');
  };

  const handleNavigateToInvoice = (invoiceNo) => {
    setAutoOpenId(invoiceNo);
    setActiveMenu('orders'); setActiveSubMenu('allFinalInvoices');
    navigate('/adminDashBoard/orders/allFinalInvoices');
  };

  const handleNavigateToOrder = (orderRefId) => {
    setAutoOpenId(orderRefId);
    setActiveMenu('orders'); setActiveSubMenu('allOrders');
    navigate('/adminDashBoard/orders/allOrders');
  };

  const handleAutoOpenConsumed = () => setAutoOpenId(null);

  const handleLogout = () => setShowLogoutConfirm(true);

  const confirmLogout = () => {
    localStorage.removeItem('adminAuthToken');
    localStorage.removeItem('adminName');
    localStorage.removeItem('adminEmail');
    localStorage.removeItem('adminRole');
    if (profileImage) URL.revokeObjectURL(profileImage);
    setShowLogoutConfirm(false);
    navigate('/adminLogin');
  };

  const cancelLogout = () => setShowLogoutConfirm(false);

  const renderContent = () => {
    // Restricted role landed on a menu it isn't allowed to see (e.g. a
    // stale bookmark) — render nothing this tick; the effect above will
    // navigate away immediately, so this just avoids a one-frame flash
    // of unauthorized content.
    if (!hasFullAccess(getAdminRoleId()) && activeMenu !== 'noAccess' && !canAccessMenu(activeMenu)) {
      return null;
    }

    switch (activeMenu) {
      case 'noAccess':
        return (
          <div style={{ padding: '60px 40px', textAlign: 'center', color: '#666' }}>
            <h2 style={{ marginBottom: '10px' }}>🚫 Access Restricted</h2>
            <p>No section has been assigned to your admin role yet. Please contact a Super Admin.</p>
          </div>
        );
      case 'dashboard':
        return (
          <DashboardSummary
            setActiveMenu={(menu) => handleMenuClick(menu)}
            setActiveSubMenu={(menu, submenu) => handleMenuClick(menu, submenu)}
          />
        );
      case 'users':
        switch (activeSubMenu) {
          case 'allAdmins': return <AllAdmins />;
          default: return <AllUsers />;
        }
      case 'orders':
        switch (activeSubMenu) {
          case 'allOrders':
            return (
              <AllOrders
                autoOpenId={autoOpenId}
                onAutoOpenConsumed={handleAutoOpenConsumed}
                onNavigateToInquiry={handleNavigateToInquiry}
                onNavigateToInvoice={handleNavigateToInvoice}
              />
            );
          case 'allFinalInvoices':
            return (
              <AllFinalPerformaInvoice
                onConvertToOrder={handleConvertInvoiceToOrder}
                onEditInvoice={handleEditInvoice}
                autoOpenId={autoOpenId}
                onAutoOpenConsumed={handleAutoOpenConsumed}
                onNavigateToInquiry={handleNavigateToInquiry}
                onNavigateToOrder={handleNavigateToOrder}
              />
            );
          case 'orderTickets':
            return <AllTickets defaultTab="order" />;
          case 'orderAnalytics': return <OrderAnalytics />;
          case 'processOrder': {
            const orderInquiryData = selectedInvoiceForOrder || selectedInquiry;
            const onBackHandler = selectedInvoiceForOrder ? handleBackFromInvoiceOrder : handleBackFromProcessOrder;
            if (!orderInquiryData) {
              return (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', padding: '60px 20px', textAlign: 'center',
                }}>
                  <h3 style={{ color: '#333', marginBottom: '10px' }}>No order selected</h3>
                  <p style={{ color: '#777', marginBottom: '20px' }}>
                    Please open "All Final Invoices" and click "Convert" on the specific invoice you want to turn into an order.
                  </p>
                  <button
                    onClick={() => { setActiveMenu('orders'); setActiveSubMenu('allFinalInvoices'); navigate('/adminDashBoard/orders/allFinalInvoices'); }}
                    style={{
                      backgroundColor: '#eb2329', color: 'white', border: 'none',
                      borderRadius: '4px', padding: '10px 24px', cursor: 'pointer', fontWeight: 600,
                    }}
                  >
                    Go to All Final Invoices
                  </button>
                </div>
              );
            }
            return (
              <ProcessOrder
                inquiryData={orderInquiryData}
                onBack={onBackHandler}
                onOrderCreated={(orderData) => {
                  setSelectedInvoiceForOrder(null);
                  setSelectedInquiry(null);
                  try { sessionStorage.removeItem('po_invoiceForOrder'); } catch (e) {}
                  try { sessionStorage.removeItem('po_selectedInquiry'); } catch (e) {}
                  handleNavigateToOrder(orderData && orderData.orderRefId);
                }}
              />
            );
          }
          default:
            return (
              <AllOrders
                autoOpenId={autoOpenId}
                onAutoOpenConsumed={handleAutoOpenConsumed}
                onNavigateToInquiry={handleNavigateToInquiry}
                onNavigateToInvoice={handleNavigateToInvoice}
              />
            );
        }
      case 'inquiries':
        switch (activeSubMenu) {
          case 'allInquiries':
            return (
              <AllInquiries
                onConvertToOrder={handleConvertToOrder}
                onSendInvoice={handleSendInvoice}
                autoOpenId={autoOpenId}
                onAutoOpenConsumed={handleAutoOpenConsumed}
                onNavigateToInvoice={handleNavigateToInvoice}
                onNavigateToOrder={handleNavigateToOrder}
              />
            );
          case 'inquiryTickets':
            return <AllTickets defaultTab="inquiry" />;
          case 'inquiryDetails': return <InquiryDetails />;
          case 'sendInvoice': {
            if (selectedInvoiceForEdit) {
              return (
                <SendFinalPerformaInvoice
                  inquiryData={selectedInvoiceForEdit}
                  onBack={handleBackFromEditInvoice}
                  onInvoiceCreated={(invoiceData) => {
                    setSelectedInvoiceForEdit(null);
                    try { sessionStorage.removeItem('sfpi_invoiceForEdit'); } catch (e) {}
                    handleNavigateToInvoice(invoiceData.proformaInvoiceNo || invoiceData.invoiceId);
                  }}
                />
              );
            }
            if (!selectedInquiryForInvoice) {
              return (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', padding: '60px 20px', textAlign: 'center',
                }}>
                  <h3 style={{ color: '#333', marginBottom: '10px' }}>No inquiry selected</h3>
                  <p style={{ color: '#777', marginBottom: '20px' }}>
                    Please open "All Inquiries" and click "Send Invoice" on the specific inquiry you want to invoice.
                  </p>
                  <button
                    onClick={() => { setActiveMenu('inquiries'); setActiveSubMenu('allInquiries'); navigate('/adminDashBoard/inquiries/allInquiries'); }}
                    style={{
                      backgroundColor: '#eb2329', color: 'white', border: 'none',
                      borderRadius: '4px', padding: '10px 24px', cursor: 'pointer', fontWeight: 600,
                    }}
                  >
                    Go to All Inquiries
                  </button>
                </div>
              );
            }
            return (
              <SendFinalPerformaInvoice
                inquiryData={selectedInquiryForInvoice}
                onBack={handleBackFromInvoice}
                onInvoiceCreated={(invoiceData) => {
                  setSelectedInquiryForInvoice(null);
                  try { sessionStorage.removeItem('sfpi_inquiryForInvoice'); } catch (e) {}
                  handleNavigateToInquiry(invoiceData.inquiryId);
                }}
              />
            );
          }
          default:
            return (
              <AllInquiries
                onConvertToOrder={handleConvertToOrder}
                onSendInvoice={handleSendInvoice}
                autoOpenId={autoOpenId}
                onAutoOpenConsumed={handleAutoOpenConsumed}
                onNavigateToInvoice={handleNavigateToInvoice}
                onNavigateToOrder={handleNavigateToOrder}
              />
            );
        }
      case 'reviews':
        return <AllReviews />;
      case 'popularCars':
        return <ManagePopularCars />;
      case 'affordableCars':
        return <ManageAffordableCars />;
      case 'cars':
        switch (activeSubMenu) {
          case 'addNewCar': return <AddNewCar />;
          case 'addNewCategory': return <AddNewCategory />;
          case 'addNewBrand': return <AddNewBrand />;
          case 'addNewAccessories': return <AddNewAccessories />;
          case 'feeList': return <FeesConfigurationList />;
          default: return <AllCarListed />;
        }
      case 'geography':
        return <GeographicalSetup />;
      case 'shipment':
        switch (activeSubMenu) {
          case 'addContainerFee': return <AddContainerFee />;
          case 'addIndividualFee': return <AddIndividualFee />;
          default: return <AddShipmentFee />;
        }
      case 'priceMargin':
        return <SetPriceMargin />;
      case 'adminProfile':
        return <AdminProfile initialTab={profileTab} />;
      case 'notifications':
        return (
          <NotificationsPage
            onBack={() => handleMenuClick('dashboard')}
            onUnreadCountChange={setBellUnreadCount}
            liveNotification={liveGlobalNotif}
            onNavigateToInquiry={handleNavigateToInquiry}
            onNavigateToOrder={handleNavigateToOrder}
          />
        );
      case 'adminChat':
        return <AdminChat onBack={() => handleMenuClick('dashboard')} onTicketRead={handleTicketMarkedRead} />;
      default:
        return (
          <DashboardSummary
            setActiveMenu={(menu) => handleMenuClick(menu)}
            setActiveSubMenu={(menu, submenu) => handleMenuClick(menu, submenu)}
          />
        );
    }
  };

  return (
    <div className="admin-dashboard">
      {showLogoutConfirm && (
        <div className="modal-overlay">
          <div className="modal-dialog logout-modal">
            <div className="modal-content">
              <div className="logout-icon">
                <svg viewBox="0 0 24 24" width="48" height="48" fill="#ff6b6b">
                  <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
                </svg>
              </div>
              <h3>Confirm Logout</h3>
              <p>Are you sure you want to logout from the admin panel?</p>
              <div className="modal-actions">
                <button className="modal-btn modal-btn-cancel" onClick={cancelLogout}>Cancel</button>
                <button className="modal-btn modal-btn-confirm logout-confirm" onClick={confirmLogout}>Logout</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="sidebar">
        <div className="logo"><h2>Admin Panel</h2></div>
        <ul className="main-menu">

          {canAccessMenu('dashboard') && (
          <li className={activeMenu === 'dashboard' ? 'active' : ''} onClick={() => handleMenuClick('dashboard')}>
            <span className="icon">🏠</span> Dashboard
          </li>
          )}

          {canAccessMenu('users') && (
          <li className={activeMenu === 'users' ? 'active' : ''} onClick={() => handleMenuClick('users', 'allUsers')}>
            <span className="icon">👥</span> Users Management
            {notifCounts.users > 0 && <span className="menu-notif-badge">{notifCounts.users > 99 ? '99+' : notifCounts.users}</span>}
            {activeMenu === 'users' && (
              <ul className="sub-menu">
                <li className={activeSubMenu === 'allUsers' ? 'active' : ''} onClick={(e) => { e.stopPropagation(); handleMenuClick('users', 'allUsers'); }}>
                  <span className="icon">👥</span> All Users
                </li>
                <li className={activeSubMenu === 'allAdmins' ? 'active' : ''} onClick={(e) => { e.stopPropagation(); handleMenuClick('users', 'allAdmins'); }}>
                  <span className="icon">🛡️</span> All Admins
                </li>
              </ul>
            )}
          </li>
          )}

          {canAccessMenu('orders') && (
          <li className={activeMenu === 'orders' ? 'active' : ''} onClick={() => handleMenuClick('orders', 'allOrders')}>
            <span className="icon">📦</span> Orders
            {notifCounts.orders > 0 && <span className="menu-notif-badge">{notifCounts.orders > 99 ? '99+' : notifCounts.orders}</span>}
            {activeMenu === 'orders' && (
              <ul className="sub-menu">
                <li className={activeSubMenu === 'allOrders' ? 'active' : ''} onClick={(e) => { e.stopPropagation(); handleMenuClick('orders', 'allOrders'); }}>
                  <span className="icon">📦</span> All Orders
                </li>
                <li className={activeSubMenu === 'allFinalInvoices' ? 'active' : ''} onClick={(e) => { e.stopPropagation(); handleMenuClick('orders', 'allFinalInvoices'); }}>
                  <span className="icon">📋</span> All Final Invoices
                </li>
                <li className={activeSubMenu === 'orderTickets' ? 'active' : ''} onClick={(e) => { e.stopPropagation(); handleMenuClick('orders', 'orderTickets'); }}>
                  <span className="icon">🎫</span> Order Tickets
                  {notifCounts.orderTickets > 0 && <span className="menu-notif-badge">{notifCounts.orderTickets > 99 ? '99+' : notifCounts.orderTickets}</span>}
                </li>
                <li className={activeSubMenu === 'orderAnalytics' ? 'active' : ''} onClick={(e) => { e.stopPropagation(); handleMenuClick('orders', 'orderAnalytics'); }}>
                  <span className="icon">📊</span> Order Analytics
                </li>
                <li
                  className={`ai-disabled-menu-item ${activeSubMenu === 'processOrder' ? 'active' : ''}`}
                  style={activeSubMenu === 'processOrder' ? {} : { cursor: 'not-allowed', opacity: 0.6 }}
                  onClick={(e) => e.stopPropagation()}
                  title={activeSubMenu === 'processOrder' ? '' : 'Select an invoice from All Final Invoices and click its Convert action instead'}
                >
                  <span className="icon">⚙️</span> Process Order
                </li>
              </ul>
            )}
          </li>
          )}

          {canAccessMenu('inquiries') && (
          <li className={activeMenu === 'inquiries' ? 'active' : ''} onClick={() => handleMenuClick('inquiries', 'allInquiries')}>
            <span className="icon">📬</span> Inquiries
            {notifCounts.inquiries > 0 && <span className="menu-notif-badge">{notifCounts.inquiries > 99 ? '99+' : notifCounts.inquiries}</span>}
            {activeMenu === 'inquiries' && (
              <ul className="sub-menu">
                <li className={activeSubMenu === 'allInquiries' ? 'active' : ''} onClick={(e) => { e.stopPropagation(); handleMenuClick('inquiries', 'allInquiries'); }}>
                  <span className="icon">📬</span> All Inquiries
                </li>
                <li className={activeSubMenu === 'inquiryTickets' ? 'active' : ''} onClick={(e) => { e.stopPropagation(); handleMenuClick('inquiries', 'inquiryTickets'); }}>
                  <span className="icon">🎫</span> Inquiry Tickets
                  {notifCounts.inquiryTickets > 0 && <span className="menu-notif-badge">{notifCounts.inquiryTickets > 99 ? '99+' : notifCounts.inquiryTickets}</span>}
                </li>
                {/* <li className={activeSubMenu === 'inquiryDetails' ? 'active' : ''} onClick={(e) => { e.stopPropagation(); handleMenuClick('inquiries', 'inquiryDetails'); }}>
                  <span className="icon">🔍</span> Inquiry Details
                </li> */}
                <li
                  className={`ai-disabled-menu-item ${activeSubMenu === 'sendInvoice' ? 'active' : ''}`}
                  style={activeSubMenu === 'sendInvoice' ? {} : { cursor: 'not-allowed', opacity: 0.6 }}
                  onClick={(e) => e.stopPropagation()}
                  title={activeSubMenu === 'sendInvoice' ? '' : 'Select an inquiry from All Inquiries and click its Send Invoice action instead'}
                >
                  <span className="icon">📄</span> Send Invoice
                </li>
              </ul>
            )}
          </li>
          )}

          {canAccessMenu('reviews') && (
          <li className={activeMenu === 'reviews' ? 'active' : ''} onClick={() => handleMenuClick('reviews', 'allReviews')}>
            <span className="icon">⭐</span> Reviews
            {notifCounts.reviews > 0 && <span className="menu-notif-badge">{notifCounts.reviews > 99 ? '99+' : notifCounts.reviews}</span>}
            {activeMenu === 'reviews' && (
              <ul className="sub-menu">
                <li className={activeSubMenu === 'allReviews' ? 'active' : ''} onClick={(e) => { e.stopPropagation(); handleMenuClick('reviews', 'allReviews'); }}>
                  <span className="icon">⭐</span> All Reviews
                </li>
              </ul>
            )}
          </li>
          )}

          {canAccessMenu('popularCars') && (
          <li className={activeMenu === 'popularCars' ? 'active' : ''} onClick={() => handleMenuClick('popularCars')}>
            <span className="icon">⭐</span> Manage Popular Cars
          </li>
          )}

          {canAccessMenu('affordableCars') && (
          <li className={activeMenu === 'affordableCars' ? 'active' : ''} onClick={() => handleMenuClick('affordableCars')}>
            <span className="icon">💰</span> Manage Affordable Cars
          </li>
          )}

          {canAccessMenu('cars') && (
          <li className={activeMenu === 'cars' ? 'active' : ''} onClick={() => handleMenuClick('cars', 'allCarListed')}>
            <span className="icon">🚗</span> Car Management
            {activeMenu === 'cars' && (
              <ul className="sub-menu">
                <li className={activeSubMenu === 'allCarListed' ? 'active' : ''} onClick={(e) => { e.stopPropagation(); handleMenuClick('cars', 'allCarListed'); }}>
                  <span className="icon">📋</span> All Cars Listed
                </li>
                <li className={activeSubMenu === 'addNewCar' ? 'active' : ''} onClick={(e) => { e.stopPropagation(); handleMenuClick('cars', 'addNewCar'); }}>
                  <span className="icon">➕</span> Add New Car
                </li>
                <li className={activeSubMenu === 'addNewCategory' ? 'active' : ''} onClick={(e) => { e.stopPropagation(); handleMenuClick('cars', 'addNewCategory'); }}>
                  <span className="icon">🏷️</span> Add New Category
                </li>
                <li className={activeSubMenu === 'addNewBrand' ? 'active' : ''} onClick={(e) => { e.stopPropagation(); handleMenuClick('cars', 'addNewBrand'); }}>
                  <span className="icon">🏭</span> Add New Brand
                </li>
                <li className={activeSubMenu === 'addNewAccessories' ? 'active' : ''} onClick={(e) => { e.stopPropagation(); handleMenuClick('cars', 'addNewAccessories'); }}>
                  <span className="iconAccessories">🧰</span> Add New Accessories
                </li>
                <li className={activeSubMenu === 'feeList' ? 'active' : ''} onClick={(e) => { e.stopPropagation(); handleMenuClick('cars', 'feeList'); }}>
                  <span className="icon">📋</span> Fees Configuration List
                </li>
              </ul>
            )}
          </li>
          )}

          {canAccessMenu('geography') && (
          <li className={activeMenu === 'geography' ? 'active' : ''} onClick={() => handleMenuClick('geography')}>
            <span className="icon">🌍</span> Geographical Setup
          </li>
          )}

          {canAccessMenu('shipment') && (
          <li className={activeMenu === 'shipment' ? 'active' : ''} onClick={() => handleMenuClick('shipment', 'addFee')}>
            <span className="icon">🚢</span> Shipment Fees
            {activeMenu === 'shipment' && (
              <ul className="sub-menu">
                <li className={activeSubMenu === 'addFee' ? 'active' : ''} onClick={(e) => { e.stopPropagation(); handleMenuClick('shipment', 'addFee'); }}>
                  <span className="icon">🚢</span> Add Port Fee
                </li>
                <li className={activeSubMenu === 'addContainerFee' ? 'active' : ''} onClick={(e) => { e.stopPropagation(); handleMenuClick('shipment', 'addContainerFee'); }}>
                  <span className="icon">📦</span> Add Container Fee
                </li>
                <li className={activeSubMenu === 'addIndividualFee' ? 'active' : ''} onClick={(e) => { e.stopPropagation(); handleMenuClick('shipment', 'addIndividualFee'); }}>
                  <span className="icon">💰</span> Add Individual Fee
                </li>
              </ul>
            )}
          </li>
          )}

          {/* ── Set Price Margin ── */}
          {canAccessMenu('priceMargin') && (
          <li className={activeMenu === 'priceMargin' ? 'active' : ''} onClick={() => handleMenuClick('priceMargin')}>
            <span className="icon">💹</span> Set Price Margin
          </li>
          )}

        </ul>
      </div>

      <div className="main-content">
        <header className="header">
          <div className="header-left"><h1>Admin Dashboard</h1></div>
          <div className="header-right">
            <div
              className="notif-msg-wrapper"
              onClick={() => handleMenuClick('adminChat')}
              style={{
                position: 'relative', cursor: 'pointer', display: 'flex', alignItems: 'center',
                justifyContent: 'center', width: '42px', height: '42px', borderRadius: '50%',
                background: '#334155', marginRight: '10px'
              }}
            >
              <span className="notif-msg-icon" style={{ fontSize: '19px', lineHeight: 1 }}>💬</span>
              {notifCounts.ticketMessages > 0 && (
                <span
                  className="notif-bell-badge"
                  style={{ position: 'absolute', top: '-4px', right: '-4px', background: '#dc2626', color: '#fff', fontSize: '11px', fontWeight: 700, borderRadius: '999px', minWidth: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px', border: '2px solid #fff' }}
                >{notifCounts.ticketMessages > 99 ? '99+' : notifCounts.ticketMessages}</span>
              )}
            </div>
            <div
              className="notif-bell-wrapper"
              onClick={() => handleMenuClick('notifications')}
              style={{
                position: 'relative', cursor: 'pointer', display: 'flex', alignItems: 'center',
                justifyContent: 'center', width: '42px', height: '42px', borderRadius: '50%',
                background: '#334155', marginRight: '16px'
              }}
            >
              <span className="notif-bell-icon" style={{ fontSize: '19px', lineHeight: 1 }}>🔔</span>
              {bellUnreadCount > 0 && (
                <span
                  className="notif-bell-badge"
                  style={{ position: 'absolute', top: '-4px', right: '-4px', background: '#dc2626', color: '#fff', fontSize: '11px', fontWeight: 700, borderRadius: '999px', minWidth: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px', border: '2px solid #fff' }}
                >{bellUnreadCount > 99 ? '99+' : bellUnreadCount}</span>
              )}
            </div>
            <div className="user-profile" onClick={() => setShowProfileDropdown(!showProfileDropdown)}>
              <div className="user-icon-wrapper">
                {profileImage ? (
                  <img src={profileImage} alt="Profile" className="user-profile-image" />
                ) : (
                  <span className="user-icon">👤</span>
                )}
              </div>
              <div className="user-info">
                <span className="user-name">{adminName}</span>
                <span className="user-role">{adminRole || 'Administrator'}</span>
              </div>
              <svg className={`dropdown-arrow ${showProfileDropdown ? 'open' : ''}`} width="12" height="12" viewBox="0 0 12 12">
                <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="2" fill="none" />
              </svg>

              {showProfileDropdown && (
                <div className="profile-dropdown">
                  <div className="profile-dropdown-header" onClick={(e) => { e.stopPropagation(); setShowProfileDropdown(false); setProfileTab('profile'); handleMenuClick('adminProfile'); }} style={{ cursor: 'pointer' }}>
                    <div className="profile-avatar-large">
                      {profileImage ? (
                        <img src={profileImage} alt="Profile" className="profile-avatar-image" />
                      ) : (
                        <span>{adminName.charAt(0).toUpperCase()}</span>
                      )}
                    </div>
                    <div className="profile-details">
                      <div className="profile-name">{adminName}</div>
                      <div className="profile-email">{adminEmail}</div>
                      <div className="profile-role-badge">{adminRole || 'Administrator'}</div>
                    </div>
                  </div>
                  <div className="profile-dropdown-divider"></div>
                  <button
                    className="profile-dropdown-item"
                    onClick={(e) => { e.stopPropagation(); setShowProfileDropdown(false); setProfileTab('profile'); handleMenuClick('adminProfile'); }}
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="#718096">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                    </svg>
                    My Profile
                  </button>
                  <button
                    className="profile-dropdown-item"
                    onClick={(e) => { e.stopPropagation(); setShowProfileDropdown(false); setProfileTab('password'); handleMenuClick('adminProfile'); }}
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="#718096">
                      <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
                    </svg>
                    Change Password
                  </button>
                  <div className="profile-dropdown-divider"></div>
                  <button
                    className="profile-dropdown-item logout-item"
                    onClick={(e) => { e.stopPropagation(); setShowProfileDropdown(false); handleLogout(); }}
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18">
                      <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
                    </svg>
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="content-area">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;